import React from 'react';
import {
  DIFFICULTY_KIND_LONG_LABEL,
  DIFFICULTY_LEVELS,
  DIFFICULTY_UNRATED_LABEL,
  difficultyHint,
  difficultyLevelInfo,
  type DifficultyKind
} from '../../utils/difficulty.js';
import { toPersianDigits } from '../../utils/timeUtils.js';
import type { DifficultyLevel } from '../../types/index.js';

export interface DifficultyChoiceRowProps {
  kind: DifficultyKind;
  value: DifficultyLevel | null;
  onChange: (v: DifficultyLevel | null) => void;
  disabled?: boolean;
}

/** UX-09: weighted row so «نامشخص» fits at 390 px with >= 44 px digit targets. */
export const DifficultyChoiceRow: React.FC<DifficultyChoiceRowProps> = ({
  kind,
  value,
  onChange,
  disabled = false
}) => {
  const options: Array<{ v: DifficultyLevel | null; label: string; flex: number }> = [
    { v: null, label: DIFFICULTY_UNRATED_LABEL, flex: 1.6 },
    ...DIFFICULTY_LEVELS.map((l) => ({
      v: l.value as DifficultyLevel | null,
      label: toPersianDigits(l.value),
      flex: 1
    }))
  ];
  const info = difficultyLevelInfo(value);

  return (
    <section data-testid={`difficulty-row-${kind}`}>
      <h3 className="text-sm font-semibold mb-2">{DIFFICULTY_KIND_LONG_LABEL[kind]}</h3>
      <div
        role="radiogroup"
        aria-label={DIFFICULTY_KIND_LONG_LABEL[kind]}
        className="flex items-stretch gap-1 p-1 bg-base-200 border border-base-300 rounded-xl"
      >
        {options.map((o) => {
          const selected = o.v === value;
          return (
            <button
              key={String(o.v)}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              data-testid={`difficulty-${kind}-${o.v ?? 'none'}`}
              onClick={() => onChange(o.v)}
              style={{ flex: o.flex }}
              className={`min-h-[44px] rounded-[9px] text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 ring-primary ${
                selected
                  ? 'bg-base-300 text-base-content shadow-sm'
                  : 'text-base-content/60 hover:text-base-content hover:bg-base-200/60'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-base-content/60 mt-2" data-testid={`difficulty-hint-${kind}`}>
        {info ? `${info.label}: ${difficultyHint(kind, value)}` : DIFFICULTY_UNRATED_LABEL}
      </p>
    </section>
  );
};

DifficultyChoiceRow.displayName = 'DifficultyChoiceRow';
